import { useState, useEffect } from 'react';
import { hospitalApi, goToLoginPage, hospitalLogout } from './hospitalApi';
import HospitalDashboard  from './pages/hospital/HospitalDashboard';
import HospitalDoctors    from './pages/hospital/HospitalDoctors';
import HospitalPatients   from './pages/hospital/HospitalPatients';
import HospitalAppointments from './pages/hospital/HospitalAppointments';
import HospitalTreatments from './pages/hospital/HospitalTreatments';
import HospitalLabReports from './pages/hospital/HospitalLabReports';
import HospitalBilling    from './pages/hospital/HospitalBilling';
import HospitalExpenses   from './pages/hospital/HospitalExpenses';
import HospitalStaff      from './pages/hospital/HospitalStaff';
import HospitalSetup      from './pages/hospital/HospitalSetup';
import HospitalUsers      from './pages/hospital/HospitalUsers';
import HospitalIPD        from './pages/hospital/HospitalIPD';
import HospitalOPD        from './pages/hospital/HospitalOPD';
import HospitalPurchases    from './pages/hospital/HospitalPurchases';
import HospitalInventory    from './pages/hospital/HospitalInventory';
import HospitalMedicineBill from './pages/hospital/HospitalMedicineBill';

const NAV = [
  { key: 'dashboard',    icon: '🏥', label: 'Dashboard' },
  { key: 'appointments', icon: '📅', label: 'Appointments' },
  { key: 'patients',     icon: '👥', label: 'Patients' },
  { key: 'treatments',   icon: '💊', label: 'Treatments' },
  { key: 'lab',          icon: '🧪', label: 'Lab Reports' },
  { key: 'billing',      icon: '💰', label: 'Billing' },
  { key: 'expenses',     icon: '📊', label: 'Expenses' },
  { key: 'doctors',      icon: '👨‍⚕️', label: 'Doctors',   adminOnly: true },
  { key: 'staff',        icon: '👩‍⚕️', label: 'Staff',     adminOnly: true },
  { key: 'setup',        icon: '⚙️', label: 'Setup',      adminOnly: true },
  { key: 'users',      icon: '👤', label: 'Users',      adminOnly: true },
  { key: 'ipd',        icon: '🛏️', label: 'IPD' },
  { key: 'opd',        icon: '🏥', label: 'OPD' },
  { key: 'purchases',    icon: '🛒', label: 'Purchases',      adminOnly: true },
  { key: 'inventory',    icon: '📦', label: 'Inventory' },
  { key: 'medicinebill', icon: '💊', label: 'Medicine Bill' },
];

const TITLES = {
  dashboard:    '🏥 Dashboard',
  appointments: '📅 Appointments',
  patients:     '👥 Patients',
  treatments:   '💊 Treatments',
  lab:          '🧪 Lab Reports',
  billing:      '💰 Billing',
  expenses:     '📊 Expenses',
  doctors:      '👨‍⚕️ Doctors',
  staff:        '👩‍⚕️ Staff',
  setup:        '⚙️ Hospital Setup',
  users:        '👤 User Management',
  ipd:          '🛏️ In-Patient Dept (IPD)',
  opd:          '🏥 Out-Patient Dept (OPD)',
  purchases:    '🛒 Purchases',
  inventory:    '📦 Inventory',
  medicinebill: '💊 Medicine Bill',
};

function initials(n) {
  if (!n) return '?';
  return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function HospitalApp({ user }) {
  const [active, setActive]                   = useState('dashboard');
  const [sidebarOpen, setSidebar]             = useState(true);
  const [isMobile, setIsMobile]               = useState(window.innerWidth < 900);
  const [drawerOpen, setDrawer]               = useState(false);
  const [hovered, setHovered]                 = useState(false);
  const [sessionExpired, setSessionExpired]   = useState(false);
  const [sessionWarning, setSessionWarning]   = useState(false);
  const [role, setRole]                       = useState('admin');
  const [ownersid, setOwnersid]               = useState('');
  const [loginForm, setLoginForm]             = useState(false);
  const [loginErr, setLoginErr]               = useState('');
  const [loginLoading, setLoginLoading]       = useState(false);

  // Listen for session-expiry events fired by hospitalApi
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('hospital-session-expired', handler);
    return () => window.removeEventListener('hospital-session-expired', handler);
  }, []);

  // Ping session every 10 minutes
  useEffect(() => {
    const ping = async () => {
      try {
        const s = await hospitalApi.getSession();
        if (!s || !s.authenticated) { setSessionExpired(true); return; }
        if (s.remainingSeconds <= 300) setSessionWarning(true);
        else setSessionWarning(false);
        if (s.role) setRole(s.role);
        if (s.ownersid) setOwnersid(s.ownersid);
      } catch (_) {}
    };
    ping();
    const id = setInterval(ping, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navigate = (key) => {
    setActive(key);
    setHovered(false);
    if (isMobile) setDrawer(false);
    else setSidebar(false);
  };

  const displayName = user?.username || user?.name || 'Staff';

  const renderPage = () => {
    const props = { role };
    switch (active) {
      case 'dashboard':    return <HospitalDashboard onNavigate={navigate} role={role} />;
      case 'appointments': return <HospitalAppointments role={role} user={{ ...(user || {}), ownersid }} />;
      case 'patients':     return <HospitalPatients {...props} />;
      case 'treatments':   return <HospitalTreatments {...props} />;
      case 'lab':          return <HospitalLabReports {...props} />;
      case 'billing':      return <HospitalBilling {...props} />;
      case 'expenses':     return <HospitalExpenses {...props} />;
      case 'doctors':      return role === 'admin' ? <HospitalDoctors {...props} /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'staff':        return role === 'admin' ? <HospitalStaff /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'setup':        return role === 'admin' ? <HospitalSetup /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'users': return role === 'admin' ? <HospitalUsers /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'ipd':       return <HospitalIPD role={role} />;
      case 'opd':       return <HospitalOPD role={role} />;
      case 'purchases': return role === 'admin' || role === 'editor' ? <HospitalPurchases role={role} /> :
        <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      case 'inventory':    return <HospitalInventory role={role} />;
      case 'medicinebill': return <HospitalMedicineBill role={role} />;
      default:             return null;
    }
  };

  const isExpanded = !isMobile && (sidebarOpen || hovered);
  const sidebarStyle = {
    width: isExpanded ? 220 : isMobile ? 220 : 60,
    minWidth: isExpanded ? 220 : isMobile ? 220 : 60,
    background: 'linear-gradient(180deg,#0f4c75 0%,#0a3555 100%)',
    height: '100vh', display: 'flex', flexDirection: 'column',
    transition: 'width .2s, min-width .2s',
    position: isMobile ? 'fixed' : 'relative',
    zIndex: isMobile ? 1200 : 1,
    left: isMobile && !drawerOpen ? -240 : 0,
    overflowY: 'auto', overflowX: 'hidden',
    boxShadow: (isMobile && drawerOpen) || isExpanded ? '4px 0 20px rgba(0,0,0,.35)' : 'none',
  };

  const showLabels = isExpanded || (isMobile && drawerOpen);

  if (sessionExpired) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg,#0f4c75 0%,#0a3555 100%)', fontFamily: 'Inter,system-ui,sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 48px', textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,.4)', maxWidth: 420, width: '90%' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#202124', marginBottom: 8 }}>Session Expired</div>
          <div style={{ fontSize: 14, color: '#5f6368', marginBottom: 20 }}>
            Your session has ended. Log in again to continue.
          </div>
          {loginErr && (
            <div style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 8,
              padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠ {loginErr}</div>
          )}
          {loginForm ? (
            <div style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', display: 'block', marginBottom: 4 }}>USERNAME</label>
                <input id="rl_user" type="text" placeholder="Staff username"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e8eaed',
                    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', display: 'block', marginBottom: 4 }}>PASSWORD</label>
                <input id="rl_pwd" type="password" placeholder="Password"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e8eaed',
                    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <button disabled={loginLoading}
                onClick={async () => {
                  const u = document.getElementById('rl_user').value.trim();
                  const p = document.getElementById('rl_pwd').value;
                  setLoginLoading(true); setLoginErr('');
                  try {
                    const res = await fetch('/spring/hospital/api/auth/login', {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ username: u, password: p }),
                    });
                    const data = await res.json();
                    if (data.ok) { window.location.reload(); }
                    else { setLoginErr(data.error || 'Invalid credentials'); }
                  } catch { setLoginErr('Network error'); }
                  finally { setLoginLoading(false); }
                }}
                style={{ width: '100%', padding: '11px', background: '#1b6ca8', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                  cursor: loginLoading ? 'default' : 'pointer', opacity: loginLoading ? 0.7 : 1 }}>
                {loginLoading ? 'Signing in…' : 'Sign In'}
              </button>
              <button onClick={() => { setLoginForm(false); setLoginErr(''); }}
                style={{ width: '100%', marginTop: 10, padding: '9px', background: 'none',
                  border: '1px solid #e8eaed', borderRadius: 8, fontSize: 13,
                  color: '#5f6368', cursor: 'pointer' }}>
                ← Back
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={goToLoginPage}
                style={{ background: '#1b6ca8', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                Admin Login
              </button>
              <button onClick={() => setLoginForm(true)}
                style={{ background: '#f4f6f9', color: '#5f6368', border: '1px solid #e8eaed', borderRadius: 8,
                  padding: '10px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Staff Login
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'Inter,system-ui,sans-serif', background: '#f4f6f9' }}>
      {/* Mobile overlay */}
      {isMobile && drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100 }}
          onClick={() => setDrawer(false)} />
      )}

      {/* Sidebar */}
      <aside style={sidebarStyle}
        onMouseEnter={() => { if (!isMobile) setHovered(true); }}
        onMouseLeave={() => { if (!isMobile) setHovered(false); }}
      >
        {/* Logo */}
        <div style={{ padding: '0 14px', height: 64, display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid rgba(255,255,255,.08)', background: '#082d47', flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#3498db,#1b6ca8)',
            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: 20 }}>
            🏥
          </div>
          {showLabels && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>SmaBizZ</div>
              <div style={{ color: '#7eb8d8', fontSize: 11, marginTop: 1 }}>Hospital Mgmt</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV.filter(item => !item.adminOnly || role === 'admin').map(item => (
            <div key={item.key}
              onClick={() => navigate(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: showLabels ? '10px 16px' : '10px 0',
                justifyContent: showLabels ? 'flex-start' : 'center',
                cursor: 'pointer', borderRadius: 8, margin: '1px 8px',
                background: active === item.key ? 'rgba(52,152,219,.25)' : 'transparent',
                borderLeft: active === item.key ? '3px solid #3498db' : '3px solid transparent',
                transition: 'background .15s',
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              {showLabels && (
                <span style={{ color: active === item.key ? '#e8f4fd' : '#7eb8d8', fontSize: 14,
                  fontWeight: active === item.key ? 700 : 500 }}>
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,.08)',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg,#1b6ca8,#3498db)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {initials(displayName)}
          </div>
          {showLabels && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: role === 'admin' ? '#3498db' : '#9aa0a6' }}>
                {role === 'admin' ? '⭐ Admin' : `🏷️ ${role}`}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Business title banner */}
        {user?.businesstitle && (
          <div style={{ width: '100%', background: 'linear-gradient(90deg,#082d47,#0f4c75)',
            textAlign: 'center', padding: '12px 24px', flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,.3)', zIndex: 20 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '.6px',
              textShadow: '0 1px 3px rgba(0,0,0,.2)' }}>
              {user.businesstitle}
            </span>
          </div>
        )}

        {/* Topbar */}
        <div style={{ height: 56, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
          background: '#fff', borderBottom: '1px solid #e8eaed',
          boxShadow: '0 1px 4px rgba(0,0,0,.08)', flexShrink: 0, zIndex: 10 }}>
          <button
            onClick={() => isMobile ? setDrawer(!drawerOpen) : setSidebar(!sidebarOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20,
              color: '#5f6368', padding: 4, flexShrink: 0 }}>
            ☰
          </button>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#202124', flex: 1 }}>
            {TITLES[active]}
          </div>
          <button onClick={hospitalLogout}
            title="Sign out"
            style={{ background: 'none', border: '1px solid #e8eaed', borderRadius: 8,
              padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: '#5f6368', display: 'flex', alignItems: 'center', gap: 5 }}>
            🚪 Sign Out
          </button>
        </div>

        {/* Session expiry warning */}
        {sessionWarning && (
          <div style={{ padding: '8px 16px', background: '#fef3c7', borderBottom: '1px solid #fbbf24',
            display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexShrink: 0 }}>
            <span>⚠️ Your session expires soon.</span>
            <button onClick={goToLoginPage}
              style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6,
                padding: '3px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Refresh Session
            </button>
            <button onClick={() => setSessionWarning(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: '#92400e', fontWeight: 700, marginLeft: 'auto' }}>
              ×
            </button>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {renderPage()}
        </div>

        {/* Footer */}
        <div style={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#f4f6f9', borderTop: '1px solid #e8eaed',
          fontSize: 11, color: '#9aa0a6', flexShrink: 0 }}>
          Hospital Management · {new Date().getFullYear()} · SmaBizZ
        </div>
      </div>
    </div>
  );
}
